import React, { useEffect, useMemo, useRef } from 'react';
import {
  Lock,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  ArrowRightLeft,
  FileText
} from 'lucide-react';
import { Button } from '../ui/button';
import { MODAL_OVERLAY_Z40_CLASS } from '../ui/modalOverlay';

const formaterDateFr = (valeur) => {
  if (!valeur) return '';
  const date = new Date(`${valeur}T00:00:00`);
  if (Number.isNaN(date.getTime())) return valeur;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};

// Tuile du recapitulatif : meme typographie pour les quatre montants du jour.
// Le liseré de couleur remplace l'icone pour distinguer les natures de montant.
const TuileRecap = ({ libelle, montant, accent, formatMontant }) => (
  <div className={`rounded-lg border border-gray-200 border-l-4 bg-white px-3 py-2.5 ${accent}`}>
    <p className="text-xs text-gray-500">{libelle}</p>
    <p className="truncate text-base font-bold text-gray-900">{formatMontant(montant)}</p>
  </div>
);

const ModalCloturerCaisse = ({
  isOpen,
  onClose,
  dateCloture,
  dateDuJour,
  recap,
  montantDeclare,
  setMontantDeclare,
  noteCloture,
  setNoteCloture,
  motifEcart,
  setMotifEcart,
  ecart,
  avecEcart,
  isClosing,
  onSubmit,
  formatMontant
}) => {
  const montantRef = useRef(null);

  const estRegularisation = Boolean(dateCloture && dateCloture !== dateDuJour);

  // Fermeture au clavier, alignee sur le comportement attendu des modals KLASYS.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !isClosing) onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isClosing, onClose]);

  // Le montant compte est la seule saisie decisive de l'ecran : on y place le focus.
  useEffect(() => {
    if (isOpen && recap) montantRef.current?.focus();
  }, [isOpen, recap]);

  const montantFormate = useMemo(() => {
    const valeur = Number(montantDeclare);
    if (!montantDeclare || Number.isNaN(valeur)) return '';
    return Math.round(valeur).toLocaleString('fr-FR').replace(/,/g, ' ');
  }, [montantDeclare]);

  if (!isOpen) return null;

  const sansEcart = !avecEcart;

  return (
    <div
      className={`${MODAL_OVERLAY_Z40_CLASS} flex items-center justify-center p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isClosing) onClose();
      }}
    >
      {/* Colonne flex : l'en-tete et le pied restent fixes, seul le corps defile. */}
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">

        {/* ================= EN-TETE ================= */}
        <div className="flex flex-shrink-0 items-start justify-between gap-4 bg-[#246990] px-6 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-white/80">
              Caisse journalière
            </p>
            <h3 className="flex items-center gap-3 text-xl font-semibold text-white">
              <Lock size={24} />
              Clôturer la caisse du jour
            </h3>
            {dateCloture && (
              <p className="mt-1.5 text-sm text-white/85">
                {estRegularisation ? 'Régularisation de la caisse du ' : 'Journée du '}
                <span className="font-semibold text-white">{formaterDateFr(dateCloture)}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isClosing}
            className="rounded-lg p-2 text-white transition-colors hover:bg-white/20 disabled:opacity-50"
            aria-label="Fermer"
          >
            <X size={24} />
          </button>
        </div>

        {!recap ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <Loader2 size={32} className="animate-spin text-[#246990]" />
            <p className="text-sm text-gray-600">Chargement du récapitulatif...</p>
          </div>
        ) : (
          <>
            {/* Corps defilant */}
            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">

              {estRegularisation && (
                <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
                  <AlertCircle size={18} className="mt-0.5 flex-shrink-0 text-orange-600" />
                  <p className="text-sm text-orange-900">
                    Vous régularisez une caisse restée ouverte. Elle doit être clôturée et remise avant
                    de pouvoir ouvrir ou utiliser la caisse du jour.
                  </p>
                </div>
              )}

              {/* ================= 1. RECAPITULATIF ================= */}
              <section>
                <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Récapitulatif de la journée
                </h4>

                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <TuileRecap
                    libelle="Solde d'ouverture"
                    montant={recap.solde_ouverture || 0}
                    accent="border-l-gray-300"
                    formatMontant={formatMontant}
                  />
                  <TuileRecap
                    libelle="Encaissements"
                    montant={recap.total_encaissements || 0}
                    accent="border-l-emerald-400"
                    formatMontant={formatMontant}
                  />
                  <TuileRecap
                    libelle="Dotations reçues"
                    montant={recap.total_dotations || 0}
                    accent="border-l-sky-400"
                    formatMontant={formatMontant}
                  />
                  <TuileRecap
                    libelle="Dépenses"
                    montant={recap.total_depenses || 0}
                    accent="border-l-rose-400"
                    formatMontant={formatMontant}
                  />
                </div>

                <div className="mt-3 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <span className="text-sm text-gray-600">Solde des opérations du jour</span>
                  <span className={`text-lg font-bold ${Number(recap.solde) >= 0 ? 'text-gray-900' : 'text-orange-600'}`}>
                    {Number(recap.solde) > 0 ? '+' : ''}{formatMontant(recap.solde || 0)}
                  </span>
                </div>
              </section>

              {/* ================= 2. CONTROLE DE CAISSE ================= */}
              <section>
                <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Contrôle de caisse
                </h4>

                <div className="rounded-lg border-2 border-[#246990]/20 bg-[#f4f9fc] p-4">
                  {/* Theorique -> compte = ecart : les trois montants sur une seule ligne */}
                  <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:gap-2">

                    <div className="rounded-lg border border-gray-200 bg-white px-4 py-2.5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Solde théorique
                      </p>
                      <p className="mt-0.5 truncate text-xl font-bold text-gray-900">
                        {formatMontant(recap.solde_theorique || 0)}
                      </p>
                      <p className="text-xs text-gray-500">Calculé par Klasys</p>
                    </div>

                    <div className="hidden items-center justify-center md:flex">
                      <span className="text-xl font-light text-gray-300">→</span>
                    </div>

                    <div className="rounded-lg border-2 border-[#246990] bg-white px-4 py-2.5">
                      <label
                        htmlFor="montant-compte-caisse"
                        className="text-xs font-semibold uppercase tracking-wide text-[#246990]"
                      >
                        Montant compté <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-0.5 flex items-baseline gap-2">
                        <input
                          id="montant-compte-caisse"
                          ref={montantRef}
                          type="number"
                          min="0"
                          step="1"
                          inputMode="numeric"
                          value={montantDeclare}
                          onChange={(e) => setMontantDeclare(e.target.value)}
                          placeholder="0"
                          className="w-full min-w-0 border-0 bg-transparent p-0 text-xl font-bold text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-0"
                        />
                        <span className="flex-shrink-0 text-sm font-semibold text-gray-500">FCFA</span>
                      </div>
                      <p className="truncate text-xs text-gray-500">
                        {montantFormate ? `${montantFormate} FCFA comptés` : 'Argent réellement compté'}
                      </p>
                    </div>

                    <div className="hidden items-center justify-center md:flex">
                      <span className="text-xl font-light text-gray-300">=</span>
                    </div>

                    {/* Ecart, recalcule en direct a chaque frappe */}
                    <div
                      className={`rounded-lg border px-4 py-2.5 ${sansEcart ? 'border-emerald-300 bg-emerald-50' : 'border-orange-300 bg-orange-50'
                        }`}
                    >
                      <p
                        className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${sansEcart ? 'text-emerald-700' : 'text-orange-700'
                          }`}
                      >
                        {sansEcart ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
                        Écart de caisse
                      </p>
                      <p
                        className={`mt-0.5 truncate text-xl font-bold ${sansEcart ? 'text-emerald-700' : 'text-orange-700'
                          }`}
                      >
                        {Number(ecart) > 0 ? '+' : ''}{formatMontant(ecart || 0)}
                      </p>
                      <p className={`truncate text-xs ${sansEcart ? 'text-emerald-600' : 'text-orange-600'}`}>
                        {sansEcart ? 'Caisse conforme' : 'Motif obligatoire'}
                      </p>
                    </div>
                  </div>

                  {avecEcart && (
                    <div className="mt-3">
                      <label
                        htmlFor="motif-ecart-caisse"
                        className="mb-1.5 block text-sm font-semibold text-orange-900"
                      >
                        Motif de l'écart <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        id="motif-ecart-caisse"
                        value={motifEcart}
                        onChange={(e) => setMotifEcart(e.target.value)}
                        placeholder="Ex : monnaie manquante, erreur de rendu, billet abîmé, transaction à vérifier..."
                        rows={2}
                        className="w-full resize-none rounded-lg border border-orange-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  )}
                </div>
              </section>

              {/* ================= 3. CONTROLES & NOTE ================= */}
              <section>
                <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Contrôles avant remise
                </h4>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-2.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100">
                          <ArrowRightLeft size={18} className="text-gray-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Transactions du jour</p>
                          <p className="text-lg font-bold text-gray-900">{recap.nb_transactions}</p>
                        </div>
                      </div>

                      <div
                        className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 ${recap.nb_pieces_manquantes > 0
                            ? 'border-orange-200 bg-orange-50'
                            : 'border-emerald-200 bg-emerald-50'
                          }`}
                      >
                        <div
                          className={`flex h-9 w-9 items-center justify-center rounded-lg ${recap.nb_pieces_manquantes > 0 ? 'bg-orange-100' : 'bg-emerald-100'
                            }`}
                        >
                          <FileText
                            size={18}
                            className={recap.nb_pieces_manquantes > 0 ? 'text-orange-600' : 'text-emerald-600'}
                          />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Pièces manquantes</p>
                          <p
                            className={`text-lg font-bold ${recap.nb_pieces_manquantes > 0 ? 'text-orange-700' : 'text-emerald-700'
                              }`}
                          >
                            {recap.nb_pieces_manquantes}
                          </p>
                        </div>
                      </div>
                    </div>

                      {recap.nb_pieces_manquantes > 0 && (
                        <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
                          <div className="flex items-start gap-3">
                            <AlertCircle size={18} className="mt-0.5 flex-shrink-0 text-orange-600" />
                            <div className="min-w-0 flex-1">
                              <h5 className="text-sm font-semibold text-orange-900">
                                {recap.nb_pieces_manquantes} dépense{recap.nb_pieces_manquantes > 1 ? 's' : ''} sans justificatif
                              </h5>
                              {/* Liste bornee : une caisse tres incomplete ne doit pas
                                  faire deborder la modale, elle defile sur place. */}
                              <ul className="mt-1.5 max-h-24 space-y-1 overflow-y-auto pr-1">
                                {(recap.transactions_sans_justificatif || []).map((transaction) => (
                                  <li
                                    key={transaction.id}
                                    className="flex items-center justify-between gap-3 text-sm text-orange-800"
                                  >
                                    <span className="truncate">{transaction.description}</span>
                                    <span className="flex-shrink-0 font-semibold">
                                      {formatMontant(transaction.montant)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Colonne droite : saisie libre et rappel, sans allonger la colonne gauche */}
                    <div className="space-y-3">
                      <div>
                        <label
                          htmlFor="note-cloture-caisse"
                          className="mb-1.5 block text-sm font-medium text-gray-700"
                        >
                          Note de clôture (optionnelle)
                        </label>
                        <textarea
                          id="note-cloture-caisse"
                          value={noteCloture}
                          onChange={(e) => setNoteCloture(e.target.value)}
                          placeholder="Ajoutez une note concernant la clôture du jour..."
                          rows={2}
                          className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                        />
                      </div>

                    <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                      <AlertCircle size={18} className="mt-0.5 flex-shrink-0 text-red-600" />
                      <div>
                        <h5 className="text-sm font-semibold text-red-900">Action irréversible</h5>
                        <p className="mt-0.5 text-sm leading-relaxed text-red-800">
                          Une fois la caisse clôturée, les transactions de cette journée ne peuvent plus
                          être modifiées ni supprimées. La remise part vers la caisse centrale.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

            </div>

            {/* ================= ACTIONS (pied fixe) ================= */}
            <div className="flex flex-shrink-0 flex-col gap-3 border-t border-gray-200 bg-white px-6 py-4 sm:flex-row">
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={onClose}
                disabled={isClosing}
              >
                Annuler
              </Button>

              <Button
                size="sm"
                className="flex-1 bg-[#246990] text-white hover:bg-[#1d5778]"
                onClick={onSubmit}
                disabled={isClosing}
              >
                {isClosing ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Clôture en cours...
                  </>
                ) : (
                  <>
                    <Lock size={16} className="mr-2" />
                    Clôturer et envoyer
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ModalCloturerCaisse;
